import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiError, eur, deDate } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Plus } from "lucide-react";

const EMPTY = { first_name: "", last_name: "", company: "", email: "", phone: "", address: "", zip: "", city: "", country: "Deutschland", notes: "" };

export default function Kunden() {
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [akte, setAkte] = useState(null);
  const [params] = useSearchParams();

  const load = () => api.get(`/customers?q=${encodeURIComponent(q)}`).then((r) => setCustomers(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => { if (params.get("neu")) setOpen(true); }, [params]);

  const save = async () => {
    try {
      await api.post("/customers", form);
      toast.success("Kunde angelegt.");
      setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openAkte = async (id) => {
    const { data } = await api.get(`/customers/${id}`);
    setAkte(data);
  };

  return (
    <div className="space-y-6" data-testid="kunden-page">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-zinc-400">CRM</div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mt-1">Kunden</h1>
        </div>
        <div className="flex gap-3">
          <Input placeholder="Name, Firma, E-Mail, Kundennr.…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} className="w-64" data-testid="customers-search" />
          <Button onClick={() => setOpen(true)} className="bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="new-customer-open-button"><Plus className="w-4 h-4 mr-1" /> Neuer Kunde</Button>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-zinc-100 text-left">
            <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400">Kundennr.</th>
            <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400">Name</th>
            <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400">Firma</th>
            <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400">Kontakt</th>
            <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400">Ort</th>
          </tr></thead>
          <tbody className="divide-y divide-zinc-50">
            {customers.length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-center text-zinc-400">Noch keine Kunden.</td></tr>}
            {customers.map((c) => (
              <tr key={c.id} onClick={() => openAkte(c.id)} data-testid={`customer-row-${c.number}`} className="hover:bg-zinc-50 cursor-pointer">
                <td className="px-5 py-3 font-mono text-xs">{c.number}</td>
                <td className="px-5 py-3">{c.first_name} {c.last_name}</td>
                <td className="px-5 py-3 text-zinc-500">{c.company || "–"}</td>
                <td className="px-5 py-3 text-zinc-500">{c.email || c.phone || "–"}</td>
                <td className="px-5 py-3 text-zinc-500">{c.city || "–"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="customer-form-dialog">
          <DialogHeader><DialogTitle>Neuer Kunde</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {Object.entries({ first_name: "Vorname", last_name: "Nachname", company: "Firma", email: "E-Mail", phone: "Telefon", address: "Adresse", zip: "PLZ", city: "Ort", country: "Land", notes: "Notizen" }).map(([k, l]) => (
              <div key={k} className={["address", "company", "notes"].includes(k) ? "col-span-2" : ""}>
                <Label>{l}</Label>
                <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`customer-form-${k}`} className="mt-1.5" />
              </div>
            ))}
          </div>
          <Button onClick={save} disabled={!form.last_name && !form.company} className="w-full bg-zinc-900 hover:bg-zinc-800 text-white mt-4" data-testid="customer-save-button">Speichern</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!akte} onOpenChange={() => setAkte(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="kundenakte-dialog">
          {akte && (
            <>
              <DialogHeader>
                <DialogTitle>Kundenakte · {akte.customer.first_name} {akte.customer.last_name} <span className="font-mono text-sm text-zinc-400">{akte.customer.number}</span></DialogTitle>
              </DialogHeader>
              <Tabs defaultValue="verkaeufe">
                <TabsList>
                  <TabsTrigger value="verkaeufe" data-testid="akte-tab-sales">Käufe ({akte.sales.length})</TabsTrigger>
                  <TabsTrigger value="rechnungen" data-testid="akte-tab-invoices">Rechnungen ({akte.invoices.length})</TabsTrigger>
                  <TabsTrigger value="artikel" data-testid="akte-tab-articles">Artikel ({akte.articles.length})</TabsTrigger>
                  <TabsTrigger value="dokumente" data-testid="akte-tab-docs">Dokumente ({akte.documents.length})</TabsTrigger>
                </TabsList>
                <div className="mt-4 text-sm">
                  <div className="grid grid-cols-2 gap-2 text-xs text-zinc-500 border border-zinc-100 rounded-lg p-3 mb-4">
                    <span>{akte.customer.address}, {akte.customer.zip} {akte.customer.city}</span>
                    <span>{akte.customer.email} · {akte.customer.phone}</span>
                  </div>
                  <TabsContent value="verkaeufe" className="space-y-1.5 mt-0">
                    {akte.sales.length === 0 && <div className="text-zinc-400">Keine Käufe.</div>}
                    {akte.sales.map((s) => (
                      <div key={s.id} className="flex justify-between border border-zinc-100 rounded-lg px-3 py-2">
                        <span className="font-mono text-xs">{s.number}</span><span className="text-xs text-zinc-400">{deDate(s.date)}</span><span className="font-medium">{eur(s.total)}</span>
                      </div>
                    ))}
                  </TabsContent>
                  <TabsContent value="rechnungen" className="space-y-1.5 mt-0">
                    {akte.invoices.length === 0 && <div className="text-zinc-400">Keine Rechnungen.</div>}
                    {akte.invoices.map((i) => (
                      <div key={i.id} className="flex justify-between border border-zinc-100 rounded-lg px-3 py-2">
                        <span className="font-mono text-xs">{i.number}</span><span className="text-xs text-zinc-400">{deDate(i.date)}</span><span className="font-medium">{eur(i.total)}</span>
                      </div>
                    ))}
                  </TabsContent>
                  <TabsContent value="artikel" className="space-y-1.5 mt-0">
                    {akte.articles.length === 0 && <div className="text-zinc-400">Keine Artikel.</div>}
                    {akte.articles.map((a) => (
                      <div key={a.id} className="flex justify-between border border-zinc-100 rounded-lg px-3 py-2">
                        <span className="font-mono text-xs">{a.number}</span><span>{a.name}</span><span className="font-medium">{eur(a.sale_price)}</span>
                      </div>
                    ))}
                  </TabsContent>
                  <TabsContent value="dokumente" className="space-y-1.5 mt-0">
                    {akte.documents.length === 0 && <div className="text-zinc-400">Keine Dokumente.</div>}
                    {akte.documents.map((d) => (
                      <div key={d.id} className="flex justify-between border border-zinc-100 rounded-lg px-3 py-2">
                        <span>{d.title}</span><span className="text-xs font-mono text-zinc-400">{d.type}</span><span className="text-xs text-zinc-400">{deDate(d.created_at)}</span>
                      </div>
                    ))}
                  </TabsContent>
                </div>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
